
import React, { useState, useEffect, useRef, useMemo, } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./jobs.scss";
import { useNavigate, useLocation } from "react-router-dom";
import { Table } from "react-bootstrap";
import { fetchJobs, deleteJob, updateJob } from "../../Redux/ActionCreators/jobsAction";
import { getUsers } from "../../Redux/ActionCreators/userAction";
import { Snackbar, Alert } from "@mui/material";

const JOB_STATUS_MAP = {
  "Published": "14107328-840b-4ad0-904f-8a2243720f70",
  "Draft": "855705f5-9696-46fc-b402-4a18ffd819af",
  "Closed": "f6fafc75-8d8e-4d72-a27c-907dd09ac6bf",
};

const JOB_TYPE_IDS = {
  "Part-Time": "64b3c119-597e-4f8a-a66a-b448fc2baf44",
  "Full-Time": "9c4ae6ff-a326-4ddb-936e-042cdd715bc2",
};

const Jobs = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);
  const searchDebounceTimer = useRef(null);

  useEffect(() => {
    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith("candidates_")) {
        sessionStorage.removeItem(key);
      }
    });
  }, []);

  const initialTab = location.state?.defaultTab || "Published";
  const [activeTab, setActiveTab] = useState(initialTab);

  const SESSION_PAGE_SIZE_KEY = useMemo(() => `jobs_pageSize_${activeTab}`, [activeTab]);
  const SESSION_CURRENT_PAGE_KEY = useMemo(() => `jobs_currentPage_${activeTab}`, [activeTab]);

  const safeGet = (key, fallback) => {
    try {
      const value = Number(window?.sessionStorage?.getItem(key));
      return Number.isFinite(value) && value > 0 ? value : fallback;
    } catch {
      return fallback;
    }
  };

  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({ createdBy: "", jobType: "", addedPeriod: "" });
  const [currentPage, setCurrentPage] = useState(() => safeGet(SESSION_CURRENT_PAGE_KEY, 1));
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [showOptions, setShowOptions] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [pageSize, setPageSize] = useState(() => safeGet(SESSION_PAGE_SIZE_KEY, 10));
  const [confirmModal, setConfirmModal] = useState({ open: false, action: "", jobId: null });

  const { jobList = [], pagination } = useSelector((state) => state.jobs || {});
  const jobsPerPage = pageSize;
  const totalPages = Math.ceil((pagination?.total || 0) / jobsPerPage);
  const loading = useSelector((state) => state.jobs.loading);
  const users = useSelector((state) => state.users?.usersList || []);

  const getFiltersAsParams = () => {
    const jobTypeId = JOB_TYPE_IDS[filters.jobType] || "";
    let fromDate = "", toDate = "";
    if (filters.addedPeriod) {
      toDate = new Date().toISOString().split("T")[0];
      const from = new Date();
      from.setDate(from.getDate() - parseInt(filters.addedPeriod));
      fromDate = from.toISOString().split("T")[0];
    }
    return {
      createdBy: filters.createdBy,
      jobTypeId,
      fromDate,
      toDate,
      search: searchTerm
    };
  };

  const refreshList = () => {
    const { createdBy, jobTypeId, fromDate, toDate, search } = getFiltersAsParams();
    dispatch(
      fetchJobs(
        currentPage,
        pageSize,
        JOB_STATUS_MAP[activeTab],
        createdBy,
        jobTypeId,
        fromDate,
        toDate,
        search
      )
    );
  };

  useEffect(() => {
    refreshList();
  }, [dispatch, activeTab, currentPage, filters, pageSize]);

  useEffect(() => {
    sessionStorage.setItem(SESSION_PAGE_SIZE_KEY, pageSize);
  }, [pageSize, SESSION_PAGE_SIZE_KEY]);

  useEffect(() => {
    sessionStorage.setItem(SESSION_CURRENT_PAGE_KEY, currentPage);
  }, [currentPage, SESSION_CURRENT_PAGE_KEY]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowOptions(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    setFilters({ createdBy: "", jobType: "", addedPeriod: "" });
    setSearchTerm("");
    sessionStorage.removeItem(`jobs_pageSize_${activeTab}`);
    sessionStorage.removeItem(`jobs_currentPage_${activeTab}`);
  };
  

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);

    clearTimeout(searchDebounceTimer.current);

    if (value.length === 0) {
      const { createdBy, jobTypeId, fromDate, toDate } = getFiltersAsParams();
      dispatch(fetchJobs(1, pageSize, JOB_STATUS_MAP[activeTab], createdBy, jobTypeId, fromDate, toDate, ""));
      setCurrentPage(1);
      return;
    }

    if (value.length >= 3) {
      searchDebounceTimer.current = setTimeout(() => {
        delaySearch(value);
      },700);
    }
  };

  const delaySearch = (value) => {
    const { createdBy, jobTypeId, fromDate, toDate } = getFiltersAsParams();
    dispatch(fetchJobs(1, pageSize, JOB_STATUS_MAP[activeTab], createdBy, jobTypeId, fromDate, toDate, value));
    setCurrentPage(1);
  };


  const handleFilterChange = (filter, value) => {
    setFilters((prev) => ({ ...prev, [filter]: value }));
    setCurrentPage(1);
  };

  const handleSelectJob = (id) => {
    setSelectedJobs((prev) =>
      prev.includes(id) ? prev.filter((jobId) => jobId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedJobs(jobList.map((job) => job?.id).filter(Boolean));
    } else {
      setSelectedJobs([]);
    }
  };

  const renderCustomPagination = (totalPages, currentPage, setCurrentPage) => {
    const pagination = [];

    pagination.push(
      <button
        className="prev-btn"
        key="prev"
        onClick={() => setCurrentPage(currentPage - 1)}
        disabled={currentPage === 1}
      >
        <img src="/images/leftarrow-icon.svg" alt="" />
      </button>
    );

    // Always show first page
    pagination.push(
      <button
        key={1}
        className={currentPage === 1 ? "active" : ""}
        onClick={() => setCurrentPage(1)}
      >
        {1}
      </button>
    );

    // Left dots
    if (currentPage > 3) {
      pagination.push(
        <button key="left-dots">
          ...
        </button>
      );
    }

    // Middle numbers (currentPage - 1, currentPage, currentPage + 1)
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    for (let i = start; i <= end; i++) {
      pagination.push(
        <button
          key={i}
          className={currentPage === i ? "active" : ""}
          onClick={() => setCurrentPage(i)}
        >
          {i}
        </button>
      );
    }

    // Right dots
    if (currentPage < totalPages - 2) {
      pagination.push(
        <button key="right-dots">
          ...
        </button>
      );
    }

    // Always show last page
    if (totalPages > 1) {
      pagination.push(
        <button
          key={totalPages}
          className={currentPage === totalPages ? "active" : ""}
          onClick={() => setCurrentPage(totalPages)}
        >
          {totalPages}
        </button>
      );
    }

    pagination.push(
      <button
        className="next-btn"
        key="next"
        onClick={() => setCurrentPage(currentPage + 1)}
        disabled={currentPage === totalPages}
      >
        <img
          src="/images/leftarrow-icon.svg"
          alt=""
          style={{ rotate: "180deg" }}
        />
      </button>
    );

    return pagination;
  };

  const handleDelete = async (id) => {
    setShowOptions(null);
    const success = await dispatch(deleteJob(id));
    if (success) {
      refreshList();
    }
  };

  const changeStatus = async (id, statusId, goToTab = null) => {
    setShowOptions(null);
    dispatch({ type: "GET_JOB_LIST_LOADING" });
    const result = await dispatch(updateJob(id, { job_status_id: statusId }));
    if (result?.success) {
      setSnackbar({ open: true, message: "Job status updated successfully", severity: "success" });
      if (goToTab) setActiveTab(goToTab);
    } else {
      setSnackbar({ open: true, message: "Action failed", severity: "error" });
      dispatch({ type: "CLEAR_LATEST_JOB_DONE" });
    }
  };

  const handlePublish = (id) => changeStatus(id, JOB_STATUS_MAP.Published, "Published");
  const handleCloseJob = (id) => changeStatus(id, JOB_STATUS_MAP.Closed, "Closed");
  const handlePublishAgain = (id) => changeStatus(id, JOB_STATUS_MAP.Published, "Published");

  const handleEdit = (jobId) => {
    navigate('/jobs/editJob', { state: { jobId } });
  };

  useEffect(() => {
    dispatch(getUsers("", 1, 100));
  }, [dispatch]);

  const getDaysFromToday = (dateString) => {
    if (!dateString) return null;
    const targetDate = new Date(dateString);
    const today = new Date();

    // Reset time parts for accuracy
    targetDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    const diffTime = targetDate - today;
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays > 0) return `${diffDays} days from now`;
    return `${Math.abs(diffDays)} days`;
  };

  const openConfirmModal = (action, jobId) => {
    setConfirmModal({ open: true, action, jobId });
  };

  const handleConfirmAction = () => {
    if (confirmModal.action === "close") {
      handleCloseJob(confirmModal.jobId);
    } else if (confirmModal.action === "publish") {
      handlePublish(confirmModal.jobId);
    }
    setConfirmModal({ open: false, action: "", jobId: null });
  };

  return (
    <div className="job-table-container">
      <div className="top-navigation">
        <div className="createjob-tab">
          <h4>Jobs</h4>
          <button
            className="create-job"
            onClick={() => navigate("/jobs/createjob")}
          >
            Create Job
          </button>
        </div>
        <div className="tab-navigation">
          {["Published", "Draft", "Closed"].map((tab) => (
            <button
              key={tab}
              className={`tab-item ${activeTab === tab ? "active" : ""}`}
              onClick={() => handleTabChange(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      <div className="table-content">
        <div className="controls">
          <div className="has-search">
            <span className="form-control-feedback">
              <img src="/images/search.svg" alt="" />
            </span>
            <input
              type="text"
              className="search-bar"
              placeholder="Search"
              value={searchTerm}
              onChange={handleSearch}
            />
          </div>
          <div className="filter-buttons">
            <select value={filters.createdBy} onChange={(e) => handleFilterChange("createdBy", e.target.value)}>
              <option value="">Created by</option>
              {users.filter(user => user?.isActive).map(user => (
                <option key={user.userID} value={user.userID}>{user.name}</option>
              ))}
            </select>
            <select value={filters.jobType} onChange={(e) => handleFilterChange("jobType", e.target.value)}>
              <option value=''>Job type</option>
              {[...new Set(jobList.map((job) => job?.job_type?.name).filter(Boolean))].map((type) => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            <select value={filters.addedPeriod} onChange={(e) => handleFilterChange("addedPeriod", e.target.value)}>
              <option value="">All Dates</option>
              <option value="30">Added in last 30 days</option>
              <option value="90">Added in last 3 months</option>
              <option value="180">Added in last 6 months</option>
              <option value="365">Added in last 1 year</option>
            </select>
          </div>
        </div>

        <Table responsive className="job-table">
          <thead>
            <tr>
              <th><input type="checkbox" onChange={handleSelectAll} style={{marginLeft:'8px'}} /></th>
              <th style={{paddingLeft:'0px'}}>Job title</th>
              <th className="text-center">Days  Added</th>
              <th className="text-center">Created By</th>
              <th className="text-center">Candidates</th>
              <th className="text-center">Shortlisted</th>
              <th className="text-center">Hired</th>
              <th className="text-center">Quick Actions</th>
              {/* <th></th> */}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="10">
                  <div className="loading-overlay" style={{alignItems:'start', color:'#F75891', height:'auto'}}>
                    <div className="spinner-border" role="status">
                      <span className="visually-hidden">Loading job summary...</span>
                    </div>
                  </div>
                </td>
              </tr>
            ) : jobList.length > 0 ? (
              jobList.map((job) => {
                if (!job || !job.id) return null;
                return (
                  <tr key={job.id}>
                    <td><input type="checkbox" style={{marginLeft:'8px'}} checked={selectedJobs.includes(job.id)} onChange={() => handleSelectJob(job.id)} /></td>
                    <td style={{paddingLeft:'0px'}}>
                      <div className="d-flex flex-column gap-1">
                        <strong
                          onClick={() => {
                            if (activeTab === "Draft") {
                              handleEdit(job.id);
                            } else if (activeTab === "Published") {
                              navigate(`/jobs/${job.id}/candidates`, { state: { jobId: job.id } });
                            }
                          }}
                          style={{cursor: activeTab === "Closed" ? "default" : "pointer"}}
                          className={activeTab === "Closed" ? "no-hover" : ""}
                        >
                          {job.title || "null"}
                        </strong>
                        <p>{job.company_name || "null"}</p>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex flex-column gap-2 text-center">
                        <strong>{getDaysFromToday(job.created_at)}</strong>
                        <p>{job.created_at ? new Date(job.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" }) : "null"}</p>
                      </div>
                    </td>
                    <td className="text-center">{job.creator?.name || "null"}</td>
                    <td 
                      className={`text-center candidate-number ${activeTab === "Draft" ? "" : "hover-effect"}`}
                      onClick={() => {
                        if (activeTab !== "Draft") {
                          navigate(`/jobs/${job.id}/candidates`, { state: { jobId: job.id } });
                        }
                      }}
                    >
                      {job.job_stats?.candidate_count || 0}
                    </td>
                    <td 
                      className={`text-center candidate-number ${activeTab === "Draft" ? "" : "hover-effect"}`}
                      onClick={() => {
                        if (job.job_stats?.shortlisted_count > 0) {
                          navigate(`/jobs/${job.id}/candidates`, {
                            state: { jobId: job.id, activateFilter: "shortlisted" },
                          });
                        }
                      }}
                    >
                      {job.job_stats?.shortlisted_count || 0}
                    </td>
                    <td 
                      className={`text-center candidate-number ${activeTab === "Draft" ? "" : "hover-effect"}`}
                      onClick={() => {
                        if (job.job_stats?.hired_count > 0) {
                          navigate(`/jobs/${job.id}/candidates`, {
                            state: { jobId: job.id, activateFilter: "hired" },
                          });
                        }
                      }}
                    >
                      {job.job_stats?.hired_count || 0}
                    </td>
                    <td style={{padding:'0'}}>
                      {activeTab === "Draft" ? (
                        <div className="d-flex justify-content-center" style={{gap:'8px'}}>
                          <button className="view-job-sm-btn" style={{border:'none'}} onClick={() => handleEdit(job.id)}>
                            <img src="/images/square-pen-black.svg" alt="" width={22} height={22} />
                          </button>
                        </div>
                      ) : (
                        <div className="d-flex justify-content-center" style={{gap:'8px'}}>
                          <button className="view-job-sm-btn" onClick={() => navigate(`/jobs/${job.id}/details`, { state: { jobId: job.id } })}>
                            <img src="/images/terms-info-icon.svg" alt="" width={18} height={18} />
                          </button>
                          {activeTab === "Closed" ? 
                            null : (
                              <button className="view-job-sm-btn" onClick={() => navigate(`/jobs/${job.id}/settings`, { state: { jobId: job.id } })}>
                                <img src="/images/settings-icon.svg" alt="" width={18} height={18} />
                              </button>
                            )
                          }
                          {activeTab === "Closed" ? 
                            (
                              <button className="view-job-sm-btn" onClick={() => openConfirmModal("publish", job.id)}>
                                <img src="/images/check-black.svg" alt="" width={14} height={14} />
                              </button>
                            ) : (
                              <button className="view-job-sm-btn" onClick={() => openConfirmModal("close", job.id)}>
                                <img src="/images/close-icon-black.svg" alt="" width={14} height={14} />
                              </button>
                            )
                          }
                        </div>
                      )}
                    </td>
                    {/* <td>
                      <div className="dropdown"  ref={showOptions === job.id ? dropdownRef : null}>
                        <button className="dots-btn" onClick={() => setShowOptions(showOptions === job.id ? null : job.id)}>
                          <img src="/images/threedots-vertical.svg" alt="..." />
                        </button>
                        {showOptions === job.id && renderRowMenu(job)}
                      </div>
                    </td> */}
                  </tr>
                );
              })
            ) : (
              <tr><td colSpan="10">No jobs found</td></tr>
            )}
          </tbody>
        </Table>
        {!loading && (
          <div className="pagination d-flex flex-wrap align-items-center justify-content-between">
            <div className="d-flex flex-wrap" style={{ gap: "40px" }}>
              <div className="d-flex flex-wrap" style={{ gap: "20px" }}>
                <div className="pagination-info d-flex align-items-center">
                  {pagination.total ? (
                    <span style={{color:'#525B66'}}>
                      Showing {(pagination.page - 1) * pagination.page_size + 1}-
                      {Math.min(
                        pagination.page * pagination.page_size,
                        pagination.total
                      )}{" "}
                      of <strong>{pagination.total}</strong> Jobs
                    </span>
                  ) : (
                    <span>Showing 0-0 of 0</span>
                  )}
                </div>
                <div className="pagination-buttons">
                  {renderCustomPagination(
                    totalPages,
                    currentPage,
                    setCurrentPage
                  )}
                </div>
              </div>
              <div className="d-flex align-items-center">
                <span style={{color:'#525B66', lineHeight:'100%'}}>Jobs per page</span>&nbsp;&nbsp;&nbsp;&nbsp;
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(parseInt(e.target.value));
                    setCurrentPage(1);
                  }}
                  style={{ width: "46px", height:'32px', borderRadius:'2px', border:'1px solid #8A9199', color:'#8A9199' }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
      {confirmModal.open && (
        <div className="upload-modal">
          <div className="modal-content" style={{ width: "435px" }}>
            <div className="modal-head">
              <div></div>
              {/* <h3>{confirmModal.action === "close" ? "Close Job" : "Publish Job Again"}</h3> */}
              <h3>Job Action</h3>
              <img
                src="/images/close-icon.svg"
                alt=""
                onClick={() => setConfirmModal({ open: false, action: "", jobId: null })}
              />
            </div>

            <div className="modal-input d-flex flex-column align-items-center" style={{ gap: "15px", padding: "15px" }}>
              <p>
                {confirmModal.action === "close"
                  ? "Are you sure you want to close this job?"
                  : "Are you sure you want to publish this job again?"}
              </p>
            </div>

            <div className="d-flex justify-content-center">
              <button className="upload-btn" onClick={handleConfirmAction}>
                {confirmModal.action === "close" ? "Close Job" : "Publish Job Again"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Jobs;
